import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Metrics come from TTButton (TTDesignKit) and .btn in the prototype: 54px tall
 * (ghost 44), 15.5px bold label, 12px radius, 0.55 opacity when disabled,
 * scale(.97) on press. Kept identical so the web does not quietly drift.
 */
type Variant = 'primary' | 'secondary' | 'ghost';

/**
 * `px-4` и `text-center` ЗАЧЕМ: подпись, переставшая помещаться в строку, переносится внутри
 * кнопки, а не упирается в её края. «Оформить и оплатить» при поднятом минимальном размере
 * шрифта — это две строки на телефоне 320 CSS-px.
 */
const BASE =
  'w-full inline-flex items-center justify-center rounded-sm px-4 text-center font-bold ' +
  'transition-transform duration-[120ms] ease-out active:scale-[0.97] ' +
  'disabled:opacity-55 disabled:pointer-events-none';

/**
 * Высота — МИНИМУМ, а не фиксированная.
 *
 * Рисуется ровно как раньше (54 px, у ghost 44) — пока подпись в одну строку, разницы нет ни в
 * пикселе. Но «Минимальный размер шрифта» в браузере поднимает кегль, НЕ трогая заданную высоту:
 * с жёстким `h-[54px]` вторая строка подписи просто вылезала за кнопку и наезжала на то, что под
 * ней. Отменить эту настройку со стороны сайта нельзя (см. блок про межстрочие в src/index.css),
 * поэтому коробка обязана расти сама. `py-3` даёт полям вырасти вместе со строкой.
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'min-h-[54px] py-3 text-[15.5px] bg-accent text-accent-ink',
  secondary: 'min-h-[54px] py-3 text-[15.5px] bg-surface-alt text-ink',
  ghost: 'min-h-[44px] py-2.5 text-[14.5px] bg-transparent text-ink-soft',
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  isLoading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  isLoading = false,
  children,
  disabled,
  className,
  ...rest
}: Props) {
  return (
    <button
      // Defaults to "button" so a Button inside a form never submits it by accident;
      // `type="submit"` is passed explicitly by the one button that means it.
      type="button"
      // A caller's className is appended, not substituted: overriding it would silently
      // drop the height, weight and press animation that make this a TTButton at all.
      className={`${BASE} ${VARIANTS[variant]} ${className ?? ''}`}
      disabled={disabled === true || isLoading}
      {...rest}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      className="inline-block h-[18px] w-[18px] animate-spin rounded-full border-2 border-current border-t-transparent"
      role="status"
      aria-label="Загрузка"
    />
  );
}
